/**
 * @wl/widget-mortgage — расчёт оферта банка. ЕДИНСТВЕННОЕ место, где виджет
 * превращает (банк, программа, ползунки, состав семьи) в платёж.
 *
 * Зачем отдельный модуль. «Что покупатель увидит» считалось в четырёх местах
 * index.ts — recalc(), getSnapshot(), promoteSpecialProgram() и строке списка —
 * и три копии уже расходились: getSnapshot, например, не знал про порог взноса
 * `minDownPct`, и в заявку мог уйти банк, которого в выдаче нет. «Семейная»
 * с 01.10.2026 сильно усложнила правило (ярус по детям, лимит, комбинированная
 * ипотека, переменный платёж), и ещё одна расходящаяся копия означала бы
 * заявку с платежом, которого покупатель на экране не видел.
 *
 * Модуль чистый: ни DOM, ни состояния виджета — его проверяют тесты без браузера.
 */

import {
  familyPayment,
  familyQuote,
  familySchedule,
  normalizeChildren,
  type FamilyPayment,
  type FamilyQuote,
  type FamilyUnavailable,
} from '@wl/shared-ui/family-mortgage';
import type { Bank, WidgetConfig } from './types';
import { calculateForBank, computePskRange, computePskRangeBySchedule, type PskRange } from './calculator';

/** Параметры расчёта: ползунки и состав семьи. */
export interface OfferParams {
  /** Стоимость недвижимости, ₽. */
  price: number;
  /** Первоначальный взнос, % от стоимости. */
  dpPct: number;
  /** Срок, лет. */
  termYears: number;
  /** Число детей (любое число; приводится к ярусу 1…5). Нужно только «Семейной». */
  children?: number;
  /** В семье есть ребёнок с инвалидностью. Нужно только «Семейной». */
  disabledChild?: boolean;
}

/** Условия «Семейной» в оффере — то, что нужно показать сверх платежа. */
export type FamilyOfferDetails = Extract<FamilyPayment, { available: true }>;

/** Оферта банка по текущей программе — единица ранжирования best-offer. */
export interface Offer {
  bank: Bank;
  /** Ставка, показанная в строке: для «Семейной» — льготная ставка семьи. */
  rate: number;
  /** Платёж, по которому ранжируем: для «Семейной» — платёж льготного периода. */
  payment: number;
  /** Сколько покупатель выплатит банку за весь срок, ₽. */
  totalPaid: number;
  psk: PskRange;
  /**
   * ПСК раскрыт самим банком (пришёл со снапшота витрины), а не посчитан нашей
   * формулой. Меняет и число, и подпись: банковский ПСК относится к продукту
   * целиком и не пересчитывается под сумму на ползунке — выдавать его за ПСК
   * конкретного кредита нельзя. У «Семейной» всегда false: банковский ПСК
   * посчитан под другую ставку.
   */
  pskFromBank: boolean;
  /** Только «Семейная» по сетке программы: льготная/рыночная части, платёж после 15 лет. */
  family?: FamilyOfferDetails;
  /**
   * Рыночная ставка этого банка, по которой он выдаёт сумму сверх лимита «Семейной».
   * Задана только когда сумма сверх лимита есть (`family.marketLoan > 0`): в подписи
   * ставки она нужна именно тогда — «10 % + 16,99 %».
   */
  marketRate?: number;
}

/** Почему по «Семейной» не нашлось ни одного предложения из-за суммы кредита. */
export interface FamilyBlock {
  reason: FamilyUnavailable;
  /** До какой суммы кредита программа доступна, ₽. */
  loanCap: number;
}

export interface OffersResult {
  /** Предложения, отсортированные по платежу: [0] — лучшее. */
  offers: Offer[];
  /** Сумма кредита, ₽. */
  principal: number;
  /** Взнос в рублях. */
  dpAmount: number;
  /** Ставка и лимит программы для этой семьи. null — программа не по сетке. */
  quote: FamilyQuote | null;
  /**
   * Банки отпали из-за суммы кредита (лимит программы / нет рыночной ставки).
   * Непусто и при `offers.length === 0` → покупателю нужна причина вместо пустоты.
   */
  blocked: FamilyBlock | null;
  /**
   * Банки, которые выдают «Семейную», но выпали из выдачи: сумма кредита выше
   * лимита, а рыночной ставки для остатка у них нет (или не хватает взноса для
   * неё). Покупатель должен знать, что банк не пропал, а не подошёл по сумме, —
   * иначе «Металлинвестбанк исчез» читается как сбой виджета.
   * Банки без самой программы и ниже порога взноса спецпредложения сюда НЕ входят:
   * они выпадают по своим условиям, а не из-за суммы.
   */
  dropped: Bank[];
}

/** Программа считается по сетке «Семейной» только если сервер прислал `config.family`. */
export function familyProgramOf(cfg: WidgetConfig, programId: string): NonNullable<WidgetConfig['family']> | null {
  return cfg.family && cfg.family.programId === programId ? cfg.family : null;
}

/**
 * Взнос и сумма кредита. Считаем в копейках: `price * pct / 100` на дробном
 * проценте даёт хвост вроде …99999999, а лимит программы сравнивается с суммой
 * кредита «больше / не больше» — на границе хвост выкинул бы банк из выдачи.
 */
export function loanFor(price: number, dpPct: number): { dpAmount: number; principal: number } {
  const dpAmount = Math.round(price * dpPct) / 100;
  return { dpAmount, principal: Math.max(0, price - dpAmount) };
}

/** Результат расчёта одного банка: оффер либо причина, по которой его нет. */
export type BankOfferResult =
  | { offer: Offer }
  | { offer: null; blocked: FamilyBlock | null };

/**
 * Оффер одного банка. Единственная функция расчёта платежа в виджете.
 *
 * Не даёт оффера (offer: null), если: банк не выдаёт программу; взнос ниже порога
 * спецпредложения; «Семейная» недоступна на этой сумме. Для двух последних
 * `blocked` объясняет причину — первый и второй случаи молчат.
 */
export function computeOffer(
  bank: Bank,
  programId: string,
  params: OfferParams,
  family: ReturnType<typeof familyProgramOf>,
  quote: FamilyQuote | null,
): BankOfferResult {
  const baseRate = bank.rates[programId];
  if (typeof baseRate !== 'number') return { offer: null, blocked: null }; // банк без программы

  // Спецпредложение с порогом взноса: покупатель поставил меньше — условие ему
  // недоступно, и мы его убираем. Показывать ставку, которую не дадут, хуже, чем
  // не показывать: человек считает по ней и приходит разочарованным.
  const needDown = bank.minDownPct?.[programId];
  if (typeof needDown === 'number' && params.dpPct < needDown) return { offer: null, blocked: null };

  const months = params.termYears * 12;
  const { dpAmount, principal } = loanFor(params.price, params.dpPct);
  const commissionPct = bank.commissions?.[programId] ?? 0;

  if (!family || !quote) {
    // Обычная программа (и «Семейная» от старого сервера без config.family).
    const payment = calculateForBank(params.price, dpAmount, params.termYears, baseRate);
    // ПСК банка приходит только для ставок, взятых с витрины. Он точнее нашей
    // формулы: в нём уже сидят страховка и удорожание после льготного периода.
    // Нет его — считаем сами, как считали всегда.
    const bankPsk = bank.marketPsk?.[programId];
    const psk = bankPsk ?? computePskRange(principal, baseRate, months, commissionPct);
    return {
      offer: { bank, rate: baseRate, payment, totalPaid: payment * months, psk, pskFromBank: !!bankPsk },
    };
  }

  // «Семейная» по сетке программы: ставка — из ярусов по числу детей, а не с
  // витрины банка и не из ручной строки кабинета (там одно число на все ярусы).
  const rate = quote.ratePct;
  // Рыночная ставка для суммы сверх лимита действует с порога взноса рыночной
  // программы банка. Тот же порог применяют витрина и чат на сервере: без него
  // калькулятор назвал бы платёж ниже, чем они, за ту же квартиру.
  const marketNeedDown = bank.combinedMinDownPct;
  const marketRatePct =
    typeof marketNeedDown === 'number' && params.dpPct < marketNeedDown ? null : (bank.combinedRatePct ?? null);
  const pay = familyPayment(family.terms, {
    principal,
    months,
    ratePct: rate,
    maxLoan: quote.maxLoan,
    marketRatePct,
  });
  if (!pay.available) return { offer: null, blocked: { reason: pay.reason, loanCap: pay.loanCap } };

  // ПСК — по фактическому графику платежей. bank.marketPsk НЕ берём: банк
  // раскрыл его под другую ставку, и для семьи с одним ребёнком он был бы занижен.
  const psk = computePskRangeBySchedule(principal, familySchedule(pay, months), commissionPct);
  return {
    offer: {
      bank,
      rate,
      payment: pay.monthly,
      totalPaid: pay.totalPaid,
      psk,
      pskFromBank: false,
      family: pay,
      ...(pay.marketLoan > 0 && marketRatePct !== null ? { marketRate: marketRatePct } : {}),
    },
  };
}

/**
 * Предложения всех банков по программе, отсортированные от лучшего к худшему.
 * Её зовут recalc, getSnapshot и promoteSpecialProgram — расходиться больше нечему.
 */
export function computeOffers(cfg: WidgetConfig, programId: string, params: OfferParams): OffersResult {
  const { dpAmount, principal } = loanFor(params.price, params.dpPct);
  const family = familyProgramOf(cfg, programId);
  const quote = family
    ? familyQuote(family.terms, {
        children: params.children,
        downPct: params.dpPct,
        disabledChild: params.disabledChild === true,
      })
    : null;

  const offers: Offer[] = [];
  const dropped: Bank[] = [];
  let blocked: FamilyBlock | null = null;
  for (const bank of cfg.banks) {
    const r = computeOffer(bank, programId, params, family, quote);
    if (r.offer) {
      offers.push(r.offer);
    } else if (r.blocked) {
      if (r.blocked.reason === 'no_market_rate') dropped.push(bank);
      // Причин может быть две у разных банков. «Больше потолка комбинированной
      // ипотеки» одинаково для всех и объясняется суммой, а не банком, —
      // она важнее, чем «у этого банка нет рыночной ставки».
      if (!blocked || r.blocked.reason === 'over_combined_limit') blocked = r.blocked;
    }
  }

  // Сортировка по возрастанию платежа (лучший = минимальный), тай-брейк по ставке.
  // При ПОЛНОМ равенстве (платёж+ставка совпали, «лучшего» нет) — Сбербанк первым,
  // дальше банки в конфиг-порядке (пожелание: если нет более выгодного
  // предложения, показываем Сбер и далее по порядку).
  offers.sort(
    (a, b) =>
      a.payment - b.payment ||
      a.rate - b.rate ||
      (a.bank.id === 'sber' ? -1 : 0) - (b.bank.id === 'sber' ? -1 : 0),
  );

  return { offers, principal, dpAmount, quote, blocked, dropped };
}

/** Снимок для заявки: то, что уходит в LeadPayload. */
export interface LeadSnapshot {
  programId: string;
  propertyCost: number;
  downPayment: number;
  termYears: number;
  monthlyPayment: number;
  bankId?: string;
  familyChildren?: number;
  familyDisabledChild?: boolean;
}

/**
 * Параметры заявки по текущему состоянию калькулятора. Платёж и банк — из того
 * же `computeOffers`, что и выдача на экране: заявка не может уйти с банком или
 * платежом, которых покупатель не видел.
 *
 * `bankId` — банк, на котором нажали «Создать заявку» (плашка списка или hero):
 * платёж берём ЕГО, а не лучшего. Раньше в заявку по Сбербанку уходил платёж
 * лучшего банка; с «Семейной» разница стала заметной — сумма сверх лимита идёт
 * по рыночной ставке каждого банка, и платежи расходятся на тысячи рублей.
 * Банка нет в выдаче (или не задан) — берём лучший оффер.
 */
export function leadSnapshot(
  cfg: WidgetConfig | null,
  programId: string,
  params: OfferParams,
  bankId?: string,
): LeadSnapshot {
  const { dpAmount } = loanFor(params.price, params.dpPct);
  let monthly = 0;
  let chosenBankId: string | undefined;
  let familyFields: Pick<LeadSnapshot, 'familyChildren' | 'familyDisabledChild'> = {};

  if (cfg) {
    const { offers } = computeOffers(cfg, programId, params);
    const chosen = (bankId !== undefined && offers.find((o) => o.bank.id === bankId)) || offers[0];
    if (chosen) {
      monthly = chosen.payment;
      chosenBankId = chosen.bank.id;
    }
    // Состав семьи — только по программе-«Семейной»: по остальным он не имеет смысла.
    if (familyProgramOf(cfg, programId)) {
      familyFields = {
        familyChildren: normalizeChildren(params.children),
        familyDisabledChild: params.disabledChild === true,
      };
    }
  }

  return {
    programId,
    propertyCost: params.price,
    downPayment: dpAmount,
    termYears: params.termYears,
    monthlyPayment: Math.round(monthly),
    ...(chosenBankId !== undefined ? { bankId: chosenBankId } : {}),
    ...familyFields,
  };
}

/**
 * Лучший платёж программы на заданных параметрах; Infinity — предложений нет.
 * Нужен promoteSpecialProgram: сравнить программы на стартовых значениях ползунков.
 */
export function bestPayment(cfg: WidgetConfig, programId: string, params: OfferParams): number {
  const best = computeOffers(cfg, programId, params).offers[0];
  return best ? best.payment : Infinity;
}

/** Границы и шаг ползунка взноса, % — как у `<input type=range>`. */
export interface DownRange {
  min: number;
  max: number;
  step: number;
}

/** Знаков после запятой у числа: 0.1 → 1, 20.1 → 1, 5 → 0. */
function decimalsOf(n: number): number {
  const frac = String(n).split('.')[1];
  return frac ? frac.length : 0;
}

/**
 * Положение ползунка взноса, при котором кредит уложится в `loanCap`: самое левое
 * из тех, что реально есть на шкале (`min + k·step`), а не расчётный процент «в
 * воздухе». Покупатель жмёт кнопку «Увеличить взнос до N ₽», и N обязано быть
 * ровно тем, что покажет ползунок после нажатия.
 *
 * `null` — такого положения нет: даже при максимальном взносе кредит выше `loanCap`
 * (или шкала не задана). Кнопку в этом случае не показываем — обещать нечего.
 */
export function raiseDownToFit(
  price: number,
  loanCap: number,
  range: DownRange,
): { dpPct: number; dpAmount: number } | null {
  if (!(price > 0) || !(range.step > 0) || !(range.max >= range.min)) return null;
  const dec = Math.max(decimalsOf(range.step), decimalsOf(range.min));
  const needPct = ((price - loanCap) / price) * 100;
  // Стартуем на шаг раньше расчётной точки (запас на округление в копейках) и
  // идём вправо до первого положения, где условие выполняется честно, по loanFor.
  let k = Math.max(0, Math.floor((needPct - range.min) / range.step) - 1);
  for (;;) {
    const dpPct = Number((range.min + k * range.step).toFixed(dec));
    if (dpPct > range.max + 1e-9) return null;
    const { dpAmount, principal } = loanFor(price, dpPct);
    if (principal <= loanCap) return { dpPct, dpAmount };
    k += 1;
  }
}

/**
 * Первая по порядку чипов программа (кроме текущей), по которой есть хоть одно
 * предложение на ТЕХ ЖЕ параметрах. `null` — другой программы с предложениями нет.
 * Порядок — как у чипов на экране (`cfg.programs`): покупателю предлагаем ближайшую
 * к той, на которой он стоит, а не лучшую по платежу — это переход, а не рекомендация.
 */
export function firstOtherProgramWithOffers(
  cfg: WidgetConfig,
  currentProgramId: string,
  params: OfferParams,
): string | null {
  for (const p of cfg.programs) {
    if (p.id === currentProgramId) continue;
    if (computeOffers(cfg, p.id, params).offers.length > 0) return p.id;
  }
  return null;
}
